// src/screens/BlitzDefense.jsx
// Blitzabfrage zur Abwehr eines Angriffs: 10 Auswahl-Fragen à 15 Sekunden aus Vokabeln
// (ab Stufe 3) und schon geübten Verbformen. Ab 80 % abgewehrt.
// Ablauf: intro → quiz → result. Fragen: src/data/blitzQuiz.js, Server: src/lib/friends.js
import React, { useEffect, useRef, useState } from "react";
import "../App.css";
import {
  BLITZ_SECONDS,
  loadBlitzQuiz,
  passScore,
  scheduleWrongWords,
} from "../data/blitzQuiz";
import { defenseTitle, finishDefense, startDefense } from "../lib/friends";
import {
  Badge,
  Button,
  Eyebrow,
  Icon,
  Note,
  OptionCard,
  Panel,
  ProgressBar,
  ScreenHeader,
  Stack,
} from "../components/ui";

const FEEDBACK_MS = 800; // so lange bleibt Richtig/Falsch sichtbar

const START_ERRORS = {
  started: "Diese Abwehr wurde schon begonnen und zählt nicht mehr.",
  expired: "Zu spät: Die 2 Stunden sind vorbei.",
  not_found: "Diesen Angriff gibt es nicht mehr.",
};

function formatRemaining(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const min = Math.ceil(ms / 60000);
  if (min < 60) return `${min} Min.`;
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")} Std.`;
}

function formatTime(iso) {
  return new Date(iso).toLocaleString("de-DE", {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BlitzDefense({
  attack,
  language,
  profileId,
  onBack,
  backLabel = "Zurück zum Hauptmenü",
}) {
  const [phase, setPhase] = useState("intro"); // intro | quiz | saving | result
  const [quiz, setQuiz] = useState(null); // { questions, progress }
  const [loadError, setLoadError] = useState(null);
  const [startError, setStartError] = useState(null);
  const [starting, setStarting] = useState(false);

  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null); // gewählte Option (null = Zeit abgelaufen)
  const [answered, setAnswered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(BLITZ_SECONDS);
  const resultsRef = useRef([]); // [{ question, right }]

  const [result, setResult] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [repeatCount, setRepeatCount] = useState(0);

  // Fragen schon vor dem Start laden, damit die Uhr erst läuft, wenn alles bereit ist
  useEffect(() => {
    let cancelled = false;
    loadBlitzQuiz(language, profileId)
      .then((q) => {
        if (cancelled) return;
        if (!q.questions.length) setLoadError("Keine Fragen gefunden.");
        setQuiz(q);
      })
      .catch((e) => {
        console.warn("loadBlitzQuiz failed:", e);
        if (!cancelled) setLoadError("Fragen konnten nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [language, profileId]);

  const questions = quiz?.questions || [];
  const total = questions.length;
  const question = questions[index];

  const handleStart = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const status = await startDefense(attack.id);
      if (status !== "ok") {
        setStartError(START_ERRORS[status] || "Start hat nicht geklappt.");
        return;
      }
      resultsRef.current = [];
      setIndex(0);
      setPicked(null);
      setAnswered(false);
      setTimeLeft(BLITZ_SECONDS);
      setPhase("quiz");
    } catch (e) {
      console.warn("startDefense failed:", e);
      setStartError("Start hat nicht geklappt. Bitte später noch einmal.");
    } finally {
      setStarting(false);
    }
  };

  const answer = (option) => {
    if (answered || !question) return;
    setPicked(option);
    setAnswered(true);
    resultsRef.current.push({ question, right: option === question.answer });
  };

  // Countdown pro Frage; bei 0 zählt die Frage als falsch
  useEffect(() => {
    if (phase !== "quiz" || answered) return;
    if (timeLeft <= 0) {
      answer(null);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, answered, timeLeft]);

  // Nach kurzer Rückmeldung zur nächsten Frage bzw. auswerten
  useEffect(() => {
    if (phase !== "quiz" || !answered) return;
    const t = setTimeout(() => {
      if (index + 1 < total) {
        setIndex(index + 1);
        setPicked(null);
        setAnswered(false);
        setTimeLeft(BLITZ_SECONDS);
      } else {
        submit();
      }
    }, FEEDBACK_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, answered, index, total]);

  const submit = async () => {
    setPhase("saving");
    const results = resultsRef.current;
    const score = results.filter((r) => r.right).length;

    const wrongVocab = results
      .filter((r) => !r.right && r.question.kind === "vocab")
      .map((r) => r.question.id);
    try {
      setRepeatCount(scheduleWrongWords(language, profileId, quiz.progress, wrongVocab).length);
    } catch (e) {
      console.warn("scheduleWrongWords failed:", e);
    }

    try {
      const res = await finishDefense(attack.id, score, total);
      setResult({ ...res, score, total });
      if (res.status !== "ok") setSaveError(START_ERRORS[res.status] || null);
    } catch (e) {
      console.warn("finishDefense failed:", e);
      setResult({ status: "error", passed: score >= passScore(total), score, total, gain: 0 });
      setSaveError("Ergebnis konnte nicht gespeichert werden.");
    }
    setPhase("result");
  };

  // ---------- Intro ----------
  if (phase === "intro") {
    return (
      <>
        <ScreenHeader
          title="Blitzabfrage"
          subtitle={`${attack.attackerName} greift dich an! Zeit zum Abwehren: ${formatRemaining(attack.expiresAt)}`}
        />
        <Panel variant="muted">
          <Stack gap={2}>
            <div>
              <strong>{total || 10} Fragen</strong>, je <strong>{BLITZ_SECONDS} Sekunden</strong>
            </div>
            <div>
              Abgewehrt ab <strong>{passScore(total || 10)} richtigen</strong> Antworten
            </div>
            <div>Belohnung: +50 Freunde-EXP, alles richtig +100, und 24 Stunden Schild</div>
            <div className="blitz-warning">
              Einmal gestartet, kannst du nicht mehr abbrechen.
            </div>
          </Stack>
        </Panel>

        <Stack gap={2} style={{ marginTop: "var(--fluent-space-5)" }}>
          {loadError && <Note tone="error">{loadError}</Note>}
          {startError && <Note tone="error">{startError}</Note>}
          <Button
            block
            onClick={handleStart}
            disabled={!quiz || !!loadError || starting || !!startError}
          >
            {!quiz && !loadError ? "Fragen werden geladen …" : starting ? "…" : (
              <>
                Los geht's <Icon name="bolt" />
              </>
            )}
          </Button>
          <Button block variant="secondary" onClick={() => onBack(false)}>
            {backLabel}
          </Button>
        </Stack>
      </>
    );
  }

  // ---------- Ergebnis ----------
  if (phase === "result" || phase === "saving") {
    if (!result) {
      return <Note>Ergebnis wird gespeichert …</Note>;
    }
    const title = defenseTitle(result.defenseStreak || 0);
    return (
      <>
        <div className={`blitz-result${result.passed ? " blitz-result--won" : ""}`}>
          <div className="blitz-result__icon">
            <Icon name={result.passed ? "shield" : "cross"} />
          </div>
          <div className="blitz-result__title">
            {result.passed ? "Angriff abgewehrt!" : "Getroffen!"}
          </div>
          <div className="blitz-result__score">
            {result.score} von {result.total} richtig
          </div>
          {result.passed && result.gain > 0 && (
            <Badge tone="sun">
              +{result.gain} Freunde-EXP{result.perfect ? " · perfekt!" : ""}
            </Badge>
          )}
        </div>

        <Panel variant="muted" style={{ marginTop: "var(--fluent-space-4)" }}>
          <Stack gap={2}>
            {result.passed ? (
              <>
                {result.shieldUntil && (
                  <div>
                    <Icon name="shield" /> Schild aktiv bis {formatTime(result.shieldUntil)}: so lange
                    kann dich niemand angreifen.
                  </div>
                )}
                <div>
                  <Icon name="counter" /> Gegenangriff: Du kannst {attack.attackerName} in den nächsten 24
                  Stunden zurück angreifen, auch wenn du heute schon angegriffen hast.
                </div>
                {result.defenseStreak > 0 && (
                  <div>
                    Abwehr-Serie: <strong>{result.defenseStreak}</strong>
                    {title && (
                      <>
                        {" · "}
                        <Icon name="medal" /> Titel „{title}“
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div>
                {attack.attackerName} bekommt +20 Freunde-EXP. Nächstes Mal klappt's!
              </div>
            )}
            {repeatCount > 0 && (
              <div>
                {repeatCount === 1
                  ? "1 falsches Wort kommt morgen zur Wiederholung."
                  : `${repeatCount} falsche Wörter kommen morgen zur Wiederholung.`}
              </div>
            )}
          </Stack>
        </Panel>

        {saveError && <Note tone="error">{saveError}</Note>}

        <Button
          block
          variant="secondary"
          style={{ marginTop: "var(--fluent-space-5)" }}
          onClick={() => onBack(true)}
        >
          {backLabel}
        </Button>
      </>
    );
  }

  // ---------- Quiz ----------
  const score = resultsRef.current.filter((r) => r.right).length;
  return (
    <>
      <div className="blitz-head">
        <Eyebrow>
          Frage {index + 1} / {total}
        </Eyebrow>
        <span className="blitz-head__score">
          {score} richtig · Ziel {passScore(total)}
        </span>
      </div>
      <ProgressBar
        className="blitz-timer"
        value={timeLeft / BLITZ_SECONDS}
        tone={timeLeft <= 5 ? "wrong" : "exp"}
        label={question.kind === "verb" ? "Verbform" : "Vokabel"}
        valueLabel={`${timeLeft} s`}
      />

      <div
        className={`blitz-question${
          answered ? (picked === question.answer ? " blitz-question--right" : " blitz-question--wrong") : ""
        }`}
      >
        <div className="blitz-question__hint">{question.hint}</div>
        <div className="blitz-question__prompt">{question.prompt}</div>
        <div className="blitz-question__mark">
          {answered && <AnswerMark key={index} right={picked === question.answer} />}
        </div>
      </div>

      <Stack gap={2} className="blitz-options">
        {question.options.map((opt) => {
          let tone;
          if (answered && opt === question.answer) tone = "right";
          else if (answered && opt === picked) tone = "wrong";
          return (
            <OptionCard
              key={opt}
              label={opt}
              tone={tone}
              disabled={answered}
              onClick={() => answer(opt)}
            />
          );
        })}
      </Stack>
      {answered && picked === null && <Note tone="error">Zeit abgelaufen!</Note>}
    </>
  );
}

// Animierter Haken (richtig) bzw. Kreuz (falsch): Kreis und Zeichen werden nacheinander gezogen
function AnswerMark({ right }) {
  return (
    <svg
      className={`answer-mark answer-mark--${right ? "right" : "wrong"}`}
      viewBox="0 0 40 40"
      role="img"
      aria-label={right ? "Richtig" : "Falsch"}
    >
      <circle className="answer-mark__circle" cx="20" cy="20" r="17" pathLength="1" />
      {right ? (
        <path className="answer-mark__line" d="M12.5 20.5l5 5 10-11" pathLength="1" />
      ) : (
        <>
          <path className="answer-mark__line" d="M14 14l12 12" pathLength="1" />
          <path className="answer-mark__line answer-mark__line--second" d="M26 14L14 26" pathLength="1" />
        </>
      )}
    </svg>
  );
}
